import React, { useState } from "react";
import {
  BookOpen, ChevronDown, Image as ImageIcon, ShoppingBag, Link2, FileText, Megaphone,
  GraduationCap, Share2, ExternalLink, DollarSign,
} from "lucide-react";

const SECTIONS = [
  {
    id: "affiliate",
    icon: Link2,
    title: "Affiliate Links — how to make money",
    body: (
      <>
        <p>Affiliate items appear in <strong>the Shop page</strong>, on the new <strong>/shop</strong> page, and inside sidebar/blog/tool pages via the recommendation strips. Add as many as you can — each one is a passive income stream.</p>
        <p className="mt-3 font-semibold">Best affiliate programs to join (all free):</p>
        <ul className="list-disc ml-5 space-y-1 mt-2 text-sm">
          <li><b>Amazon Associates</b> — <a className="text-indigo-600 hover:underline" target="_blank" rel="noreferrer" href="https://affiliate-program.amazon.com/">affiliate-program.amazon.com</a> — books, gear, everything.</li>
          <li><b>Impact.com</b> — <a className="text-indigo-600 hover:underline" target="_blank" rel="noreferrer" href="https://impact.com/">impact.com</a> — Shopify, Canva, Envato, Airbnb.</li>
          <li><b>PartnerStack</b> — <a className="text-indigo-600 hover:underline" target="_blank" rel="noreferrer" href="https://partnerstack.com/">partnerstack.com</a> — most SaaS tools (Notion, Webflow, Framer, etc.).</li>
          <li><b>ShareASale</b> — <a className="text-indigo-600 hover:underline" target="_blank" rel="noreferrer" href="https://shareasale.com/">shareasale.com</a> — WP Engine, Grammarly, marketing tools.</li>
          <li><b>CJ Affiliate</b> — <a className="text-indigo-600 hover:underline" target="_blank" rel="noreferrer" href="https://cj.com/">cj.com</a> — GoDaddy, Fiverr, big brands.</li>
          <li><b>Hostinger / Bluehost / SiteGround</b> — direct programs, 60% commissions on hosting.</li>
          <li><b>ClickBank & Digistore24</b> — digital info-products, high commissions.</li>
          <li><b>Gumroad, ThriveCart, Kajabi</b> — for course/creator affiliate deals.</li>
          <li><b>YouTube memberships & shorts fund, TikTok Shop, Instagram broadcast channels</b> — link your affiliate offers from social.</li>
        </ul>
        <p className="mt-3 font-semibold">To add one:</p>
        <ol className="list-decimal ml-5 space-y-1 mt-1 text-sm">
          <li>Go to <b>Affiliate Links</b> tab → <b>New</b>.</li>
          <li>Fill: <em>Name, Category, Image URL, Description, Affiliate URL</em> (your tracking link).</li>
          <li>Leave price at <b>0</b> — affiliate items show a &quot;View Deal&quot; button, not a price.</li>
          <li>Category tip: use one of <em>Website Resources · Marketing Resources · AI Prompt Collections · Templates · Books · Merchandise</em> so it slots into <b>/shop</b> automatically.</li>
        </ol>
      </>
    ),
  },
  {
    id: "products",
    icon: ShoppingBag,
    title: "Shop (Affiliate Products)",
    body: (
      <>
        <p>Your Shop is <b>affiliate-only</b>: every item links out to a partner store (Amazon Associates, Impact, PartnerStack, ShareASale&hellip;). Set the price to 0, paste your tracking URL, done.</p>
        <ul className="list-disc ml-5 space-y-1 mt-2 text-sm">
          <li>Items appear on the <b>/shop</b> page and in blog/tool sidebars automatically.</li>
          <li>Use the <b>Amazon Bulk Import</b> tool to import products from Amazon URLs in one paste.</li>
          <li>When your Shopify store is ready, its products will slot in alongside these picks.</li>
        </ul>
        <p className="mt-3 font-semibold">Image URLs:</p>
        <p className="text-sm">Use hosted images. Free sources: <a className="text-indigo-600 hover:underline" href="https://unsplash.com" target="_blank" rel="noreferrer">Unsplash</a>, <a className="text-indigo-600 hover:underline" href="https://pexels.com" target="_blank" rel="noreferrer">Pexels</a>.</p>
      </>
    ),
  },
  {
    id: "blog",
    icon: FileText,
    title: "Blog & Writing — publishing tips",
    body: (
      <>
        <p>Use <b>AI Write</b> to generate a full first-draft article. Then edit before saving.</p>
        <ul className="list-disc ml-5 space-y-1 mt-2 text-sm">
          <li><b>Slug</b> — SEO-friendly URL (use the <em>SEO Slug Generator</em> tool for perfect results).</li>
          <li><b>Content</b> — use <code>## Heading</code> and <code>###</code> for subheadings; blank lines between paragraphs.</li>
          <li><b>Tags</b> — comma-separated. Helps discovery.</li>
          <li><b>Featured</b> — show on the Home page.</li>
          <li><b>Monetize</b>: naturally sprinkle affiliate links inside the post using standard Markdown <code>[text](url)</code>.</li>
        </ul>
      </>
    ),
  },
  {
    id: "courses",
    icon: GraduationCap,
    title: "Mason Academy — Courses",
    body: (
      <>
        <p>Every course you add gets a Stripe checkout button. Fill fields honestly and keep price rounded (e.g. $49, $99, $199).</p>
        <ul className="list-disc ml-5 space-y-1 mt-2 text-sm">
          <li><b>Objectives</b> — comma-separated list of what students learn.</li>
          <li><b>Duration & Lessons</b> — visible on the course card.</li>
          <li><b>Featured</b> — pins the course on Home / Academy top row.</li>
        </ul>
      </>
    ),
  },
  {
    id: "ads",
    icon: Megaphone,
    title: "Ads (Sponsored placements)",
    body: (
      <>
        <p>Sell ad space to sponsors or use these slots for your own affiliate offers (double-up revenue).</p>
        <ul className="list-disc ml-5 space-y-1 mt-2 text-sm">
          <li><b>Placement</b> — <em>sidebar</em> (blog/writing) or <em>blog</em> (in-content).</li>
          <li><b>Image URL</b> — 600×400 or 1200×628 works well.</li>
          <li><b>Link URL</b> — your affiliate/tracking link — every click is tracked in Overview.</li>
          <li>Pricing tip: for sold ads charge $50–$500/mo depending on traffic (see Overview → total views).</li>
        </ul>
      </>
    ),
  },

  {
    id: "adsense",
    icon: DollarSign,
    title: "Google AdSense — step by step",
    body: (
      <>
        <ol className="list-decimal ml-5 space-y-1.5 text-sm">
          <li>Create an account at <a className="text-indigo-600 hover:underline" href="https://adsense.google.com" target="_blank" rel="noreferrer">adsense.google.com</a> with your Google account.</li>
          <li>When asked for your site, enter <b>juliusmason.com</b>.</li>
          <li>Google gives you a verification snippet — you do <b>not</b> need to paste code anywhere. Just copy your publisher ID (<code>ca-pub-…</code>) into <b>Social &amp; Profile → AdSense Publisher ID</b> below and save. The script loads site-wide automatically.</li>
          <li>Update <b>/ads.txt</b>: replace the placeholder with <code>google.com, pub-YOURID, DIRECT, f08c47fec0942fa0</code> (your ID is in AdSense → Account → Settings). Ask me to do this in one message.</li>
          <li>Wait for Google's review (typically a few days to 2 weeks). You'll get an email when approved.</li>
          <li>After approval: in AdSense → Ads → Overview, turn on <b>Auto Ads</b> for hands-free revenue, and/or create a <b>Display ad unit</b> named "Blog in-article" (format: In-article or Display) and paste its 10-digit slot ID into <b>AdSense Blog Slot</b> below — it renders inside every blog article automatically.</li>
          <li>Watch the money: AdSense → Reports. Calibrate the RPM dial in <b>Overview → Est. ad revenue</b> from your real reports.</li>
        </ol>
        <p className="mt-3 text-xs text-muted-foreground">Approval tips: the site must be live on your real domain with real content (already true), a privacy policy helps, and Google pays out from $100 by bank transfer (set this up in AdSense → Payments).</p>
      </>
    ),
  },

  {
    id: "social",
    icon: Share2,
    title: "Social & Profile",
    body: (
      <>
        <p>Fill every social URL — the icons appear in the footer and on About. Broken/empty ones are hidden automatically.</p>
        <p className="mt-2 text-sm">Tip: cross-link your affiliate landing pages from every bio (Instagram, YouTube description, TikTok, X). More placements = more clicks.</p>
      </>
    ),
  },
  {
    id: "keys",
    icon: DollarSign,
    title: "Payments & API Keys",
    body: (
      <>
        <p>Your live keys live in <code>backend/.env</code>. To go live:</p>
        <ul className="list-disc ml-5 space-y-1 mt-2 text-sm">
          <li><b>Stripe</b> — swap the sandbox key for your production secret key at <a className="text-indigo-600 hover:underline" href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer">dashboard.stripe.com/apikeys</a>.</li>
          <li><b>Resend (emails)</b> — already managed for you; contact/newsletter emails just work.</li>
          <li><b>AI (chatbot / blog writer / design studio)</b> — powered by your Emergent Universal Key. Top up in your Emergent Profile if it runs low.</li>
        </ul>
      </>
    ),
  },
  {
    id: "images",
    icon: ImageIcon,
    title: "Where to find images",
    body: (
      <>
        <ul className="list-disc ml-5 space-y-1 text-sm">
          <li><a className="text-indigo-600 hover:underline" href="https://unsplash.com" target="_blank" rel="noreferrer">Unsplash</a> — free stock photos.</li>
          <li><a className="text-indigo-600 hover:underline" href="https://pexels.com" target="_blank" rel="noreferrer">Pexels</a> — free stock photos & videos.</li>
          <li><a className="text-indigo-600 hover:underline" href="https://www.pixabay.com" target="_blank" rel="noreferrer">Pixabay</a> — free graphics & illustrations.</li>
          <li>Right-click any image → <em>Copy image address</em> → paste into the Image URL field.</li>
          <li>To host your own: use <a className="text-indigo-600 hover:underline" href="https://imgbb.com" target="_blank" rel="noreferrer">ImgBB</a> or <a className="text-indigo-600 hover:underline" href="https://cloudinary.com" target="_blank" rel="noreferrer">Cloudinary</a>.</li>
        </ul>
      </>
    ),
  },
];

function Section({ s, open, onToggle }) {
  const Icon = s.icon;
  return (
    <div className="border border-border">
      <button data-testid={`help-toggle-${s.id}`} onClick={onToggle} className="w-full flex items-center justify-between p-5 hover:bg-secondary/40 transition text-left">
        <span className="flex items-center gap-3"><Icon className="w-5 h-5 text-primary" /><span className="font-display text-lg">{s.title}</span></span>
        <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div data-testid={`help-body-${s.id}`} className="px-5 pb-6 pt-1 border-t border-border text-foreground/80 leading-relaxed">{s.body}</div>}
    </div>
  );
}

export default function AdminHelp() {
  const [open, setOpen] = useState("affiliate");
  return (
    <div data-testid="admin-help">
      <div className="flex items-center gap-3 mb-6">
        <BookOpen className="w-7 h-7 text-primary" />
        <h2 className="font-display text-3xl">Help & Instructions</h2>
      </div>
      <p className="text-muted-foreground max-w-2xl mb-8">Everything you need to run this site, add content, and monetise with affiliate links — no developer required.</p>

      <div className="p-5 bg-primary/10 border border-primary/30 mb-8">
        <p className="overline text-primary mb-2">Quick start · maximise revenue</p>
        <ol className="list-decimal ml-5 space-y-1 text-sm">
          <li>Sign up for <b>Amazon Associates</b> and <b>Impact.com</b> — takes 10 minutes.</li>
          <li>Add 10–20 affiliate products via <b>Affiliate Links</b> tab. Aim for 3 per category.</li>
          <li>Write 1 blog post per week using <b>AI Write</b>, sprinkling in your affiliate links naturally.</li>
          <li>Publish a course in Academy — highest-ticket item on the site.</li>
          <li>Post a link to <b>/shop</b> in every social bio.</li>
        </ol>
      </div>

      <div className="space-y-3">
        {SECTIONS.map((s) => <Section key={s.id} s={s} open={open === s.id} onToggle={() => setOpen(open === s.id ? null : s.id)} />)}
      </div>

      <div className="mt-10 p-6 bg-secondary/40 border border-border">
        <p className="overline text-primary mb-2">Field cheatsheet</p>
        <ul className="text-sm space-y-1 text-foreground/80">
          <li><b>Slug</b> — a URL-friendly ID. Use lowercase, no spaces (e.g. <code>my-first-post</code>). Try the SEO Slug tool.</li>
          <li><b>Category</b> — a group label. Keep it consistent so filters work well.</li>
          <li><b>Tags</b> — comma-separated keywords for search & related posts.</li>
          <li><b>Image URL</b> — must be a direct link ending in .jpg/.png/.webp.</li>
          <li><b>Featured</b> — pins content on the home page.</li>
        </ul>
      </div>
    </div>
  );
}
